import { Button } from '@mui/material';
import { mat4 } from 'gl-matrix';
import { closeSnackbar, type SnackbarKey, type VariantType } from 'notistack';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { getRegistrationUndoService } from '~/domain/ServiceContainer';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { useImageStreamProgress } from '~/hooks/image/useImageStreamProgress';
import { useActivateRegistration } from '~/hooks/registration/useRegistrationStore';
import {
    RegistrationFailure,
    registerRigid,
} from '~/lib/registration-engine/registerRigid';
import { pendingAutoRegistrationState } from '~/state/atoms/toolsAtoms';
import { handleError } from '~/utils/errors';
import { enqueueSnackbar } from '~/utils/notifications';

/**
 * Mutual information below which a fit from no starting alignment is not
 * applied. On the clinical benchmark, fits on the wrong anatomy reach down to
 * 0.01 and every fit within 20 mm of the clinical match is at least 0.14.
 */
const MIN_TRUSTED_MUTUAL_INFORMATION = 0.12;

/** The pair a primary's row shows, by identity. */
function rowFusion(primarySeriesUid: string) {
    const entry = getDatasetEnvironmentRegistry().entryFor(primarySeriesUid);
    return {
        secondary: entry?.secondaryImage,
        registration: entry?.registration,
    };
}

/**
 * Aligns the registration waiting in {@link pendingAutoRegistrationState}
 * with no starting alignment, then fuses its pair, so the pair is first
 * shown aligned. A notice says a registration is running for as long as one
 * waits, and its Cancel ends the wait with the pair at its starting
 * alignment. The run starts once both images are complete; when either never
 * will be, is PET (which the engine cannot read), or the run fails, the pair
 * is shown with its starting alignment and the user is told; likewise when
 * the fit shares too little information between the images to be trusted. The
 * fit is applied with an undo point before it, and not at all when the
 * registration was edited or approved while it ran. The pair is fused only if the
 * primary's row still shows what it showed when the wait began; otherwise
 * the user is told the registration is ready in the list. A wait whose
 * registration or images are gone is dropped, with no result applied. The
 * report of a fit stays until dismissed or until the patient changes.
 */
export function useRunPendingAutoRegistration() {
    const intl = useIntl();
    const session = useSessionOrNull();
    const [pending, setPending] = useRecoilState(pendingAutoRegistrationState);
    const activateRegistration = useActivateRegistration();

    const patient = session?.patient;
    const primary = pending
        ? patient?.images.find((i) => i.seriesUid === pending.primarySeriesUid)
        : undefined;
    const secondary = pending
        ? patient?.images.find(
              (i) => i.seriesUid === pending.secondarySeriesUid
          )
        : undefined;
    const registration = pending
        ? patient?.getRegistrationWithSopInstanceUid(
              pending.registrationSopInstanceUid
          )
        : undefined;
    const primaryProgress = useImageStreamProgress(primary);
    const secondaryProgress = useImageStreamProgress(secondary);
    const complete = primaryProgress.isComplete && secondaryProgress.isComplete;
    const neverComplete =
        primaryProgress.isIncompleteFinal ||
        secondaryProgress.isIncompleteFinal;

    // Read when the run ends, so a re-render mid-run never restarts it.
    const intlRef = useRef(intl);
    intlRef.current = intl;
    const activateRef = useRef(activateRegistration);
    activateRef.current = activateRegistration;
    const rowAtStartRef = useRef<ReturnType<typeof rowFusion> | null>(null);
    const cancelRef = useRef(() => {});
    const fitReportsRef = useRef(new Set<SnackbarKey>());

    useEffect(() => {
        const fitReports = fitReportsRef.current;
        return () => {
            fitReports.forEach((report) => closeSnackbar(report));
            fitReports.clear();
        };
    }, [patient]);

    useEffect(() => {
        if (pending !== null) {
            rowAtStartRef.current = rowFusion(pending.primarySeriesUid);
        }
    }, [pending]);

    useEffect(() => {
        if (pending === null) {
            return;
        }
        const notice = enqueueSnackbar(
            intl.formatMessage({ id: 'registration.auto.registeringNewImage' }),
            {
                variant: 'info',
                persist: true,
                action: () => (
                    <Button
                        size="small"
                        color="inherit"
                        onClick={() => cancelRef.current()}
                    >
                        {intl.formatMessage({ id: 'cancel' })}
                    </Button>
                ),
            }
        );
        return () => closeSnackbar(notice);
    }, [pending, intl]);

    useEffect(() => {
        if (pending === null) {
            return;
        }
        if (!patient || !primary || !secondary || !registration) {
            setPending(null);
            return;
        }
        const notify = (id: string, variant: VariantType) =>
            enqueueSnackbar(intlRef.current.formatMessage({ id }), {
                variant,
            });
        // Looked up again when the run ends: a delete does not end the wait.
        const present = () =>
            patient.getRegistrationWithSopInstanceUid(
                registration.sopInstanceUid
            ) === registration &&
            patient.images.includes(primary) &&
            patient.images.includes(secondary);
        const show = () => {
            setPending(null);
            const row = rowFusion(primary.seriesUid);
            // The user opened this registration during the wait.
            if (row.registration === registration) {
                return;
            }
            const atStart = rowAtStartRef.current;
            if (
                row.secondary !== atStart?.secondary ||
                row.registration !== atStart?.registration
            ) {
                notify('registration.auto.readyInList', 'info');
                return;
            }
            activateRef.current(
                registration.sopInstanceUid,
                secondary.seriesUid,
                primary
            );
        };
        if (primary.hasWidePixelRange || secondary.hasWidePixelRange) {
            notify('registration.auto.petUnavailable', 'warning');
            show();
            return;
        }
        if (neverComplete) {
            notify('registration.auto.imageIncomplete', 'warning');
            show();
            return;
        }
        let ended = false;
        const abort = new AbortController();
        const end = () => {
            ended = true;
            abort.abort();
            cancelRef.current = () => {};
        };
        cancelRef.current = () => {
            end();
            if (!present()) {
                setPending(null);
                return;
            }
            notify('registration.auto.cancelled', 'info');
            show();
        };
        if (!complete) {
            return end;
        }
        // A matrix edited or approved while the run was going is the user's
        // and is kept; the fit is then not applied.
        const startRevision = registration.transformRevision;
        registerRigid(
            primary,
            secondary,
            registration.transformation,
            null,
            true,
            abort.signal
        )
            .then((fitted) => {
                if (ended || !present()) {
                    return;
                }
                if (
                    registration.transformRevision !== startRevision ||
                    registration.isApproved
                ) {
                    notify('registration.auto.editedMeanwhile', 'info');
                    return;
                }
                if (fitted.mutualInformation < MIN_TRUSTED_MUTUAL_INFORMATION) {
                    notify('registration.auto.lowConfidence', 'warning');
                    return;
                }
                getRegistrationUndoService().pushState(
                    registration.dicomUid,
                    registration.transformation
                );
                // Through the Environment showing it, if one already does, so
                // every view of the registration moves with it.
                const showing = getDatasetEnvironmentRegistry()
                    .residentEnvironments()
                    .find((e) => e.registration === registration);
                if (showing) {
                    showing.setRegistrationMatrix(fitted.matrix);
                } else {
                    mat4.copy(registration.transformation, fitted.matrix);
                }
                registration.syncTransformationToDataset();
                // A fit from no starting alignment can be confidently wrong,
                // so its report asks for a check and stays until dismissed.
                fitReportsRef.current.add(
                    enqueueSnackbar(
                        intlRef.current.formatMessage({
                            id:
                                fitted.runnerUp === null
                                    ? 'registration.auto.automaticCompleted'
                                    : 'registration.auto.automaticCompletedWithRunnerUp',
                        }),
                        { variant: 'warning', persist: true }
                    )
                );
            })
            .catch((error: unknown) => {
                if (ended || !present()) {
                    return;
                }
                handleError(
                    error,
                    error instanceof RegistrationFailure
                        ? intlRef.current.formatMessage(
                              { id: 'registration.auto.newImageFailedBecause' },
                              { reason: error.reason }
                          )
                        : intlRef.current.formatMessage({
                              id: 'registration.auto.newImageFailed',
                          })
                );
            })
            .finally(() => {
                if (ended) {
                    return;
                }
                end();
                if (present()) {
                    show();
                } else {
                    setPending(null);
                }
            });
        return end;
    }, [
        pending,
        patient,
        primary,
        secondary,
        registration,
        complete,
        neverComplete,
        setPending,
    ]);
}
